import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { TestResult } from '../../../shared/test.types';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { TranslationService } from '../../../../services/translation.service';
import { LoginService } from '../../../../services/login.service';
import { SharpStateService } from '../../services/sharp-state.service';
import { SharpAudioService } from '../../services/sharp-audio.service';
import { COLORS_MAP } from '../../sharp-v2.types';

type Phase = 'intro' | 'memorize' | 'interrupted';
type ChipKey = 'name' | 'color' | 'object' | 'place';

interface ScenePart {
  kind: 'text' | 'chip';
  text: string;
  bg?: string;
  color?: string;
  // Stagger for the word-by-word reveal (handoff 2026-08-17): each part fades in
  // 0.45s after the previous one, so the sentence builds up rather than appearing
  // all at once. prefers-reduced-motion collapses the stagger (global token rule).
  delay?: number;
}

// Background tint matches reference inline-style on each <strong> chip
// (ref used literal rgba(209,64,156,...); brand-aware equivalent below).
const CHIP_BG_DEFAULT = 'rgba(var(--brand-rgb,209,64,156),.08)';
const CHIP_BG_COLOR = 'rgba(var(--brand-rgb,209,64,156),.06)';

// Hebrew grammatical gender of the recall nouns (keyed by English pool id).
// Drives subject-verb agreement (name → verb) and noun-adjective agreement
// (object → colour). Only consulted when rendering in Hebrew.
const NAME_GENDER: Record<string, 'm' | 'f'> = {
  sarah: 'f', david: 'm', rachel: 'f', michael: 'm', emma: 'f',
  james: 'm', lisa: 'f', robert: 'm', anna: 'f', thomas: 'm',
};
const OBJECT_GENDER: Record<string, 'm' | 'f'> = {
  umbrella: 'f', notebook: 'f', clock: 'm', basket: 'm',
  ring: 'f', envelope: 'f', candle: 'm', scarf: 'm',
};

@Component({
  selector: 'app-recall-encode',
  standalone: true,
  imports: [CommonModule, MatButtonModule, TranslatePipe],
  templateUrl: './recall-encode.component.html',
  styleUrls: ['./recall-encode.component.scss'],
})
export class RecallEncodeComponent implements OnInit, OnDestroy {
  @Input() testId = 'recall-encode';
  @Output() testComplete = new EventEmitter<TestResult>();

  phase: Phase = 'intro';
  timer = 12;
  readonly encodeTime = 12;
  sceneParts: ScenePart[] = [];
  private timerHandle: any = null;
  private startTime = 0;

  constructor(
    private state: SharpStateService,
    private audio: SharpAudioService,
    private translation: TranslationService,
    private login: LoginService
  ) {}

  ngOnInit(): void {
    this.state.resetSession();
    this.startTime = Date.now();
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  ngOnDestroy(): void {
    this.clearTimer();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  private readonly onVisibility = () => {
    if (document.visibilityState === 'hidden') this.interrupt();
  };

  /**
   * Correction brief 3.3: the 12 seconds are the encoding exposure, so leaving the
   * screen mid-way meant she memorised a shorter look than everyone else and the
   * recall later read that as forgetting. The interrupted exposure is thrown away
   * and a NEW sentence is shown from the start (a fresh draw, so the voided look
   * is not a free extra rehearsal of the same items). This is the first task of
   * the battery, so re-drawing the session items is safe.
   */
  private interrupt(): void {
    if (this.phase !== 'memorize') return;
    this.clearTimer();
    this.sceneParts = [];
    this.phase = 'interrupted';
  }

  resumeAfterInterrupt(): void {
    if (this.phase !== 'interrupted') return;
    this.state.resetSession();
    this.reveal();
  }

  get colorHex(): string {
    return COLORS_MAP[this.state.recallItems.color] || 'var(--sharp-accent)';
  }

  get timerPct(): number {
    return (this.timer / this.encodeTime) * 100;
  }

  /**
   * Parse the i18n `sentenceHtml` template (e.g. "{{name}} left a {{color}} {{object}} at the {{place}}.")
   * into an ordered list of text + chip parts so we can render real <strong> elements
   * with inline-style bindings (Angular's sanitizer would strip styles from [innerHTML]).
   */
  private buildSceneParts(): ScenePart[] {
    const template = this.translation.translate(
      'cognitive.sharpV2.recallEncode.sentenceHtml',
      { defaultValue: '{{name}} left a {{color}} {{object}} at the {{place}}.' }
    );
    const ri = this.state.recallItems;
    // Hebrew gender agreement: the colour adjective agrees with the object's
    // gender (feminine object → feminine colour, e.g. מחברת צהובה not צהוב), and
    // the verb agrees with the subject/name (תומאס הניח, שרה הניחה). English has
    // no gendered forms, so this is a no-op there.
    const heb = this.translation.isRTL();
    const objFem = heb && OBJECT_GENDER[ri.object] === 'f';
    const nameFem = NAME_GENDER[ri.name.toLowerCase()] === 'f';
    const chipText: Record<ChipKey, string> = {
      name: this.translation.translate(
        `cognitive.sharpV2.recall.names.${ri.name.toLowerCase()}`,
        { defaultValue: ri.name }
      ),
      color: this.translation.translate(
        `cognitive.sharpV2.recall.colors${objFem ? 'F' : ''}.${ri.color}`,
        { defaultValue: ri.color }
      ),
      object: this.translation.translate(
        `cognitive.sharpV2.recall.objects.${ri.object}`,
        { defaultValue: ri.object }
      ),
      place: this.translation.translate(
        `cognitive.sharpV2.recall.places.${ri.place}`,
        { defaultValue: ri.place }
      ),
    };
    const verbText = heb
      ? this.translation.translate(
          `cognitive.sharpV2.recall.verb.${nameFem ? 'f' : 'm'}`,
          { defaultValue: nameFem ? 'הניחה' : 'הניח' }
        )
      : '';

    const parts: ScenePart[] = [];
    const regex = /\{\{\s*(name|verb|color|object|place)\s*\}\}/g;
    let lastIdx = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(template)) !== null) {
      if (m.index > lastIdx) {
        parts.push({ kind: 'text', text: template.slice(lastIdx, m.index) });
      }
      const key = m[1];
      if (key === 'verb') {
        // The verb is plain narration, not one of the memorised chips.
        parts.push({ kind: 'text', text: verbText });
      } else {
        const chipKey = key as ChipKey;
        parts.push({
          kind: 'chip',
          text: chipText[chipKey],
          bg: chipKey === 'color' ? CHIP_BG_COLOR : CHIP_BG_DEFAULT,
          color: chipKey === 'color' ? this.colorHex : undefined,
        });
      }
      lastIdx = regex.lastIndex;
    }
    if (lastIdx < template.length) {
      parts.push({ kind: 'text', text: template.slice(lastIdx) });
    }
    // Word-by-word reveal: stagger each part 0.45s after the previous.
    parts.forEach((p, i) => (p.delay = +(i * 0.45).toFixed(2)));
    return parts;
  }

  reveal(): void {
    this.audio.prime();
    this.sceneParts = this.buildSceneParts();
    this.phase = 'memorize';
    this.timer = this.encodeTime;
    this.runTimer();
  }

  private runTimer(): void {
    this.clearTimer();
    this.timerHandle = setInterval(() => {
      this.timer--;
      if (this.timer <= 0) {
        this.clearTimer();
        this.finish();
      }
    }, 1000);
  }

  private clearTimer(): void {
    if (this.timerHandle) {
      clearInterval(this.timerHandle);
      this.timerHandle = null;
    }
  }

  private finish(): void {
    const result: TestResult = {
      testId: this.testId,
      response: {
        recallItems: { ...this.state.recallItems },
      },
      responseTime: Date.now() - this.startTime,
      timestamp: Date.now(),
    };
    this.testComplete.emit(result);
  }

  isAdmin(): boolean {
    return this.login.isAdmin();
  }

  canSkipTests(): boolean {
    return this.login.canSkipTests();
  }

  skipTimer(): void {
    if (!this.canSkipTests() || this.phase !== 'memorize') return;
    this.clearTimer();
    this.timer = 0;
    this.finish();
  }
}
